// Copyright 2018 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { MeasurementProcessorTypes } from "datatypes/MeasurementProcessorTypes";
import { VideoFrame } from "datatypes/SoramaLocalApi";
import { GenericMeasurement, MeasurementPart, MeasurementPartType } from "datatypes/SoramaWebApi";
import { buildQueryString } from "extensions/UriExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler, WebRequestPriority } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IMeasurementVideoFileService } from "services/Storage/IMeasurementVideoFileService";

@autoinject
export class LocalVideoFileService implements IMeasurementVideoFileService {
    private readonly webRequestHandler: IWebRequestHandler;
    private readonly localApi: ILocalApi;

    constructor(webRequestHandler: IWebRequestHandler, localApi: ILocalApi) {
        this.webRequestHandler = webRequestHandler;
        this.localApi = localApi;
    }

    public getVideoFrames(
        measurement: GenericMeasurement,
        partIndex?: number,
        callback?: (progress: number) => void,
        startTime?: number,
        duration?: number
    ): Promise<VideoFrame[][]> {
        let offset = 0;

        if (startTime == undefined) {
            startTime = 0;
        }

        if (
            measurement.isComputedMeasurement &&
            measurement.computedMeasurementProperties.ProcessorType == MeasurementProcessorTypes.timeRange
        ) {
            startTime += measurement.computedMeasurementProperties.Properties.TimeRange.From;
            offset = measurement.computedMeasurementProperties.Properties.TimeRange.From;
        }

        const queryString = buildQueryString({ startTime: startTime, duration: duration });
        let videoParts: MeasurementPart[];

        // Get video parts requested
        if (partIndex !== undefined && partIndex !== null) {
            if (partIndex >= measurement.parts.length || partIndex < 0)
                return Promise.reject("Part index is out of range");

            if (measurement.parts[partIndex].measurementType !== MeasurementPartType.Video)
                return Promise.reject("Measurement part is not of video type");

            videoParts = [measurement.parts[partIndex]];
        } else {
            videoParts = measurement.parts.filter((x) => x.measurementType === MeasurementPartType.Video);
        }

        return this.localApi.videoFile
            .then((uri) => {
                // Get video data
                const promises: Promise<VideoFrame[]>[] = [];
                if (measurement.sorId) {
                    // Get frames from local sor file
                    promises.push(
                        this.webRequestHandler
                            .createGetRequest<VideoFrame[]>(
                                uri + "/Sor/Frames" + queryString + "&sorId=" + measurement.sorId
                            )
                            .withProgress(callback)
                            .withPriority(WebRequestPriority.LowPriority)
                            .send()
                            .then((x) => x.content)
                    );
                } else {
                    for (const part of videoParts) {
                        // Get frames for this datapart
                        promises.push(
                            this.webRequestHandler
                                .createGetRequest<VideoFrame[]>(
                                    uri + "/" + part.dataContainer + "/Frames" + queryString
                                )
                                .withProgress(callback)
                                .withPriority(WebRequestPriority.LowPriority)
                                .send()
                                .then((x) => x.content)
                        );
                    }
                }

                // Wait for all to finish
                return Promise.all(promises).catch((e) => {
                    const error = new Error("Video data is not available");
                    // This status code is used in the farfieldmodule's updateData function
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (error as any).statusCode = e.statusCode;
                    throw error;
                });
            })
            .then((x) => {
                // Return for the offset
                return x.map((y) => {
                    return y.map((z) => {
                        z.time -= offset;
                        return z;
                    });
                });
            });
    }
}
